import { codeUnitString } from "./code-unit-string.js";
import { z } from "zod";
//#region src/app-source.ts
/**
* The app source record and the per-release build facts (plan 2026-10-07-app-source-record, C-1
* and C-2).
*
* The app source record says where an app's code lives: Git or not, the repository, the origin
* remote, the full ref HEAD named and the commit it resolved to, and the path of the app inside the
* checkout. The `zion` CLI detects it from a checkout (`zion app create`, `zion app update`,
* `zion app release`). The CP stores it on `release.apps` and sets `detectedAt` from its own clock
* (D7, D23).
*
* The per-release build facts say what one release was built from: the commit sha, the dirty
* flag, and the SDK versions of the app (D10, D13, D14). The SDK versions are the `@zion/*`
* versions of the app's pnpm lockfile importers, `zion-sdk` from a `uv.lock` and
* `zion-sdk-runtime` from a `Cargo.lock` (plan 2026-10-18-dynamic-blob-eligibility, D3a). They
* travel as bundle annotations (D12) and the CP stores them on `release.app_compositions`.
*
* One declaration, every door (D19). The release-engine DTOs, the MCP tool arguments, the bundle
* parser and the CLI import these schemas. A hand-kept copy is the drift this module exists to
* prevent.
*
* Every schema here validates and never rewrites. A zod `.transform()` on a schema that a
* controller binds throws in `createOpenApiDocument()` at boot, so there is none.
*
* The root barrel is browser-safe (`barrel-browser-safe.test.ts`). This module uses only zod and
* the `URL` global, which Node 24 and every browser define.
*/
/** The two values of `vcs`: a Git checkout, or a directory outside Git (D7). */
const APP_SOURCE_VCS = ["git", "none"];
/** The longest `repo` the CP stores, in characters (overview security table). */
const APP_SOURCE_REPO_MAX = 140;
/** The longest `origin` the CP stores, in characters (overview security table). */
const APP_SOURCE_ORIGIN_MAX = 512;
/**
* The prefix of a branch ref. A `ref` on a branch is this prefix plus the branch name, never the
* short name alone, so `main` and `refs/heads/main` are two different values and only the second
* is valid (D23).
*/
const APP_SOURCE_BRANCH_REF_PREFIX = "refs/heads/";
/** The `ref` of a detached HEAD: the literal `HEAD` (D23). */
const APP_SOURCE_DETACHED_REF = "HEAD";
/**
* The longest `ref` the CP stores, in UTF-16 code units: {@link APP_SOURCE_BRANCH_REF_PREFIX} (11)
* plus a branch name of at most 200 (overview security table). A letter outside ASCII counts as
* one, and a character outside the Basic Multilingual Plane counts as two.
*/
const APP_SOURCE_REF_MAX = 211;
/** The longest `path` the CP stores, in characters (overview security table). */
const APP_SOURCE_PATH_MAX = 512;
/**
* The `repo` grammar: `<owner>/<name>` with letters, digits, `.`, `_` and `-` in each part. It is
* the grammar of `REPO_SLUG_PATTERN` in `tools/zion-app/src/status/origin-remote.ts`, so a value
* that `repoSlugFromRemoteUrl` returns matches it (D5). `isAppSourceRepo` also refuses a part that
* is `.` or `..`, as `assertRepoSlug` does, and a value over the length bound, so a few values
* `repoSlugFromRemoteUrl` returns fail the full check. Case is kept as detected. No `g` flag: `.test()` on a `/g`
* regex keeps state across calls.
*/
const APP_SOURCE_REPO_PATTERN = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;
/** A commit object id: sha-1 (40) or sha-256 (64) lower-case hex (C-1 `sha`, C-2 `sourceSha`). */
const APP_SOURCE_SHA_PATTERN = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;
/** The most entries an SDK versions record holds (D15). */
const APP_SDK_VERSIONS_MAX_ENTRIES = 64;
/** The longest version string an SDK versions record holds, in characters (D15). */
const APP_SDK_VERSION_MAX = 64;
/**
* An npm package name an SDK versions record may hold: `@zion/` plus 1 to 64 of `[a-z0-9-]` (D15).
* The `zion-app` CLI selects the importers of `pnpm-lock.yaml` with it. The record also admits
* {@link APP_SDK_PYTHON_PACKAGE} and {@link APP_SDK_RUST_PACKAGE}; {@link APP_SDK_NAME_PATTERN} is
* the grammar of every key.
*/
const APP_SDK_PACKAGE_PATTERN = /^@zion\/[a-z0-9-]{1,64}$/;
/**
* The name of the Python SDK in an SDK versions record: the `zion-sdk` package of a `uv.lock`
* (plan 2026-10-18-dynamic-blob-eligibility, D3a). The `zion-app` CLI writes it, and release-engine
* reads it for the Python floor of the blob eligibility rule.
*/
const APP_SDK_PYTHON_PACKAGE = "zion-sdk";
/**
* The name of the Rust SDK in an SDK versions record: the `zion-sdk-runtime` crate of a
* `Cargo.lock` (plan 2026-10-18-dynamic-blob-eligibility, D3a). The `zion-app` CLI writes it, and
* release-engine reads it for the Rust floor of the blob eligibility rule.
*/
const APP_SDK_RUST_PACKAGE = "zion-sdk-runtime";
/**
* The grammar of every key of an SDK versions record: a name that {@link APP_SDK_PACKAGE_PATTERN}
* accepts, or exactly {@link APP_SDK_PYTHON_PACKAGE}, or exactly {@link APP_SDK_RUST_PACKAGE}. Any
* other name is refused, so a third non-npm name needs a change here. No `g` flag.
*/
const APP_SDK_NAME_PATTERN = /^(?:@zion\/[a-z0-9-]{1,64}|zion-sdk|zion-sdk-runtime)$/;
/**
* A semver 2.0.0 version, `MAJOR.MINOR.PATCH` with an optional pre-release and build part. No
* leading `v`. Each group ends at a fixed delimiter, so the match time is linear in the length.
*/
const APP_SDK_VERSION_PATTERN = /^(?:0|[1-9][0-9]*)\.(?:0|[1-9][0-9]*)\.(?:0|[1-9][0-9]*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
/**
* A character that no branch name in a `ref` may hold. Git's `check-ref-format` refuses an ASCII
* control character, DEL, a space, `~`, `^`, `:`, `?`, `*`, `[` and `\`. The CP also refuses every
* Unicode control, format, surrogate and separator character (`\p{Cc}`, `\p{Cf}`, `\p{Cs}`,
* `\p{Z}`). A bidi override, a zero-width mark or a no-break space can make one ref read as another
* in a terminal or in the console. A lone surrogate does not survive the UTF-8 write. Git accepts
* every other character, `@`, `+` and every letter outside ASCII among them.
*/
const BRANCH_FORBIDDEN_CHARACTER = /[~^:?*[\\\p{Cc}\p{Cf}\p{Cs}\p{Z}]/u;
/** The longest branch name after {@link APP_SOURCE_BRANCH_REF_PREFIX}, in UTF-16 code units. */
const BRANCH_NAME_MAX = 200;
/** The origin schemes the CP stores (D4). `http` is absent on purpose. */
const ORIGIN_SCHEMES = /* @__PURE__ */ new Set([
	"https:",
	"ssh:",
	"git:"
]);
/** A scheme URL: `<scheme>://`. */
const SCHEME_URL = /^[A-Za-z][A-Za-z0-9+.-]*:\/\//;
/**
* The scp form `[user@]host:path`. The user is `[A-Za-z0-9._-]+`, so a `user:password@` prefix
* cannot match it. The host is two or more characters, so a Windows drive letter (`C:/x`) is a
* local path and fails. The path holds letters, digits and `._~/+-` only, so it holds no `@`, no
* `:` and no `\`.
*/
const SCP_ORIGIN = /^(?:[A-Za-z0-9._-]+@)?([A-Za-z0-9][A-Za-z0-9.-]*[A-Za-z0-9]):([A-Za-z0-9._~/+-]+)$/;
/**
* The host of an accepted origin, in both arms: a name that starts and ends with a letter or a
* digit and holds letters, digits, `.` and `-` between, or a bracketed IPv6 address.
*/
const ORIGIN_HOST = /^(?:[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?|\[[0-9A-Fa-f:.]+\])$/;
/**
* Hosts the scp form refuses: URL scheme names. `file:/srv/x.git` and `http:github.com/x` match the
* scp shape with the host `file` or `http`, and each reads as a scheme the CP refuses (D4, R-009).
*/
const SCP_SCHEME_HOSTS = /* @__PURE__ */ new Set([
	"about",
	"blob",
	"data",
	"file",
	"ftp",
	"ftps",
	"git",
	"http",
	"https",
	"javascript",
	"mailto",
	"rsync",
	"ssh",
	"vbscript",
	"ws",
	"wss"
]);
/**
* A scheme URL whose authority is not empty in the text itself. The WHATWG parser turns the first
* path segment of `https:///srv/x.git` into the host, so the parsed `hostname` cannot tell that the
* written value had no host.
*/
const SCHEME_URL_WITH_AUTHORITY = /^[A-Za-z][A-Za-z0-9+.-]*:\/\/[^/]/;
/**
* The only characters an origin may hold: the RFC 3986 unreserved and reserved characters and
* `%`, less `?` and `#`. So a space, a quote, `<`, `>`, `\`, a backtick, a control character and
* every character outside ASCII are refused by construction.
*/
const ORIGIN_CHARACTERS = /^[A-Za-z0-9\-._~:/[\]@!$&'()*+,;=%]+$/;
/**
* A character a stored path must not hold: a control or format character (C0, DEL, C1, bidi
* controls, zero-width marks), a lone surrogate, or a separator other than the ASCII space (a
* no-break space, a line or paragraph separator). The ref refuses the same classes (R-009).
*/
const PATH_FORBIDDEN_CHARACTER = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/u;
/**
* True when `value` is a `repo` the CP stores: it matches {@link APP_SOURCE_REPO_PATTERN}, it is at
* most {@link APP_SOURCE_REPO_MAX} characters, and neither part is `.` or `..`. Pure.
*/
function isAppSourceRepo(value) {
	return value.length <= 140 && APP_SOURCE_REPO_PATTERN.test(value) && value.split("/").every((part) => part !== "." && part !== "..");
}
/**
* True when `value` is an `origin` the CP stores (D4). The CLI calls it before it sends a detected
* origin, and sends `null` for an origin that fails it, so a create never fails on an origin.
*
* Accepted:
* - a scheme URL with the scheme `https`, `ssh` or `git` (any case), a host, and no user and no
*   password: `https://github.com/acme/demo.git`, `ssh://github.com/acme/demo.git`;
* - the scp form `[user@]host:path`: `git@github.com:acme/demo.git`. The scp form has no password
*   syntax.
*
* Refused:
* - a value over {@link APP_SOURCE_ORIGIN_MAX} characters;
* - a character outside the RFC 3986 URI characters and `%`: a space, a quote, `<`, `>`, `\`,
*   a control character, a character outside ASCII;
* - a `?` or a `#` anywhere, because a query can carry a token (`?access_token=`);
* - a scheme URL with a user or a password (`https://user:token@host/x`, `ssh://git@host/x`),
*   with no host, or with any other scheme (`http:`, `file:`, `ext::`);
* - every other form, which includes every local path (`/srv/x.git`, `./x`, `../x`, `~/x`, `C:/x`).
*
* Pure. Never throws.
*/
function isAppSourceOrigin(value) {
	return parseOrigin(value) !== null;
}
/**
* The one parse of an origin: its lower-case host and its path, or null for a value that
* {@link isAppSourceOrigin} refuses. A scheme URL must carry its authority in the written text
* ({@link SCHEME_URL_WITH_AUTHORITY}); with one, the WHATWG parser either throws or gives a host,
* so no accepted URL has an empty host.
*/
function parseOrigin(value) {
	if (value.length > 512 || !ORIGIN_CHARACTERS.test(value)) return null;
	if (SCHEME_URL.test(value)) {
		if (!SCHEME_URL_WITH_AUTHORITY.test(value)) return null;
		let url;
		try {
			url = new URL(value);
		} catch {
			return null;
		}
		if (!ORIGIN_SCHEMES.has(url.protocol) || url.username !== "" || url.password !== "") return null;
		if (!ORIGIN_HOST.test(url.hostname)) return null;
		return {
			host: url.hostname.toLowerCase(),
			path: url.pathname
		};
	}
	const scp = SCP_ORIGIN.exec(value);
	if (scp === null) return null;
	const host = scp[1].toLowerCase();
	return SCP_SCHEME_HOSTS.has(host) ? null : {
		host,
		path: scp[2]
	};
}
/**
* The host and the `<owner>/<name>` of an origin that {@link isAppSourceOrigin} accepts, or null for
* any other value. A reader that shows a link for a stored record uses it to check that the stored
* `repo` is the one the origin names (R-009), so no reader declares the origin grammar again. Pure.
* Never throws.
*/
function appSourceOriginParts(origin) {
	const parsed = parseOrigin(origin);
	if (parsed === null) return null;
	const repo = parsed.path.replace(/^\/+/, "").replace(/\/+$/, "").replace(/\.git$/, "");
	return {
		host: parsed.host,
		repo: isAppSourceRepo(repo) ? repo : null
	};
}
/**
* True when `value` is a `ref` the CP stores (D23). Two forms, and nothing else:
* - the literal {@link APP_SOURCE_DETACHED_REF} (`HEAD`), for a detached HEAD;
* - {@link APP_SOURCE_BRANCH_REF_PREFIX} (`refs/heads/`) plus a branch name that Git's
*   `check-ref-format` accepts. So the record of every branch that Git can hold keeps its ref and
*   its sha: `refs/heads/dependabot/npm_and_yarn/@types/node-20.1.0`, `refs/heads/feat/a+b` and
*   `refs/heads/feat/café` are valid.
*
* The branch name rules, Git's first:
* - 1 to 200 UTF-16 code units, so the whole ref is at most {@link APP_SOURCE_REF_MAX};
* - no empty `/`-separated segment, so no leading `/`, no trailing `/` and no `//`;
* - no segment that starts with `.` or ends with `.lock`;
* - no `..`, no `@{`, and no `.` at the end;
* - no ASCII control character, DEL, space, `~`, `^`, `:`, `?`, `*`, `[` or `\`;
* - for the CP alone, no Unicode control, format, surrogate or separator character (a bidi
*   override, a zero-width mark, a no-break space), so one ref never reads as another;
* - for the CP alone, no `refs/` at the start, which a ref with the prefix written twice holds.
*
* Refused: a short branch name (`main`), a tag or remote ref (`refs/tags/v1`,
* `refs/remotes/origin/main`), a revision expression (`HEAD~1`, `refs/heads/main@{1}`), any other
* case of `HEAD`, and a value over {@link APP_SOURCE_REF_MAX} code units. Pure. Never throws.
*/
function isAppSourceRef(value) {
	if (value === "HEAD") return true;
	if (!value.startsWith("refs/heads/")) return false;
	const branch = value.slice(11);
	return branch.length >= 1 && branch.length <= BRANCH_NAME_MAX && !branch.startsWith("refs/") && !branch.endsWith(".") && !branch.includes("..") && !branch.includes("@{") && !BRANCH_FORBIDDEN_CHARACTER.test(branch) && branch.split("/").every((segment) => segment !== "" && !segment.startsWith(".") && !segment.endsWith(".lock"));
}
/**
* True when `value` is a `path` the CP stores (D3): `.` for the root of the checkout, or one or
* more `/`-separated segments relative to it (`apps/witnessai-mcp`). Refused: an empty value, a
* value over {@link APP_SOURCE_PATH_MAX} UTF-16 code units, a leading or trailing `/`, an empty segment, a
* `.` or `..` segment, a `\`, a control or format character, a lone surrogate, and a separator
* other than the ASCII space. Pure.
*/
function isAppSourcePath(value) {
	if (value === ".") return true;
	return value.length >= 1 && value.length <= 512 && !value.includes("\\") && !PATH_FORBIDDEN_CHARACTER.test(value) && value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..");
}
const repoSchema = z.string().max(140).refine(isAppSourceRepo, { message: "repo must be <owner>/<name>: letters, digits, dot, underscore and hyphen" }).describe("The repository as <owner>/<name>, parsed from the origin. Null when the origin names none.");
const originSchema = z.string().max(512).refine(isAppSourceOrigin, { message: "origin must be an https, ssh or git URL with no user and no password, or the scp form host:path; a local path, a file: URL, a query and a fragment are refused" }).describe("The origin remote URL without credentials. Null when the checkout has no origin.");
const refSchema = codeUnitString().max(211).refine(isAppSourceRef, { message: "ref must be HEAD or refs/heads/<branch>, never a short branch name; the branch must pass git check-ref-format and hold no control, format or space character" }).describe("The full ref HEAD named: refs/heads/<branch>, or HEAD when detached. Null when HEAD was unreadable.");
/** A commit object id. See {@link APP_SOURCE_SHA_PATTERN}. */
const appSourceShaSchema = z.string().regex(APP_SOURCE_SHA_PATTERN);
const headShaSchema = appSourceShaSchema.describe("The commit HEAD resolved to, 40 or 64 lower-case hex. Null for a branch with no commit and when HEAD was unreadable.");
const pathSchema = codeUnitString().max(512).refine(isAppSourcePath, { message: "path must be '.' or a relative path with '/' separators and no '..' segment" }).describe("The app directory relative to the checkout root, '.' at the root.");
const gitInputShape = {
	vcs: z.literal("git"),
	repo: repoSchema.nullable(),
	origin: originSchema.nullable(),
	ref: refSchema.nullable(),
	sha: headShaSchema.nullable(),
	path: pathSchema
};
/**
* True when `ref` and `sha` describe one state of HEAD that the detector can read (D23):
* - `ref` null (HEAD unreadable) and `sha` null;
* - `ref` `HEAD` (detached) and a `sha`, because a detached HEAD holds the commit itself;
* - `ref` `refs/heads/<branch>` and a `sha`, or a null `sha` for a branch with no commit yet.
* So a sha with no ref, and a detached HEAD with no sha, are refusals.
*/
function refAndShaAgree(record) {
	if (record.ref === null) return record.sha === null;
	if (record.ref === "HEAD") return record.sha !== null;
	return true;
}
/** The issue of a {@link refAndShaAgree} refusal. It names `sha`, the key that disagrees. */
const REF_SHA_ISSUE = {
	message: "sha must be null when ref is null, and present when ref is HEAD",
	path: ["sha"]
};
/** A detected-at instant: an ISO-8601 date-time with an offset, which the CP's clock sets. */
const detectedAtSchema = z.iso.datetime({ offset: true }).describe("When the CP stored the record, from the CP clock (ISO-8601).");
/**
* The app source record as a client sends it: `POST /v1/apps`, `PATCH /v1/apps/:id`, the MCP
* `create_app` and `update_app` arguments, and the `zion.app.build.source` bundle annotation
* (C-1, D21, D23).
*
* A discriminated union on `vcs`:
* - `{ vcs: 'git', repo, origin, ref, sha, path }`. All six keys are required. `repo`, `origin`,
*   `ref` and `sha` are nullable: no origin gives a null `repo` and `origin` (D5); an unreadable
*   HEAD gives a null `ref` and `sha`; a branch with no commit gives a null `sha` (defaults table,
*   detection row). `ref` is `refs/heads/<branch>` or `HEAD`, never a short name, so a branch and
*   a detached HEAD never read alike. `path` is never null.
* - `{ vcs: 'none' }` for a directory outside Git, with no other key.
*
* Both arms are `.strict()`, so an unknown key, a `detectedAt` or a `branch` among them, is a
* refusal: the CP sets the instant, never the client. Each field refuses a value outside its
* predicate ({@link isAppSourceRepo}, {@link isAppSourceOrigin}, {@link isAppSourceRef},
* {@link APP_SOURCE_SHA_PATTERN}, {@link isAppSourcePath}). The git arm also refuses a `sha` with a
* null `ref`, and a `ref` of `HEAD` with a null `sha`, with an issue on `sha`. At the API a refusal
* is a 400. At the bundle parser a refusal leaves the app record as it was and adds a publish
* warning (D15, D21).
*/
const appSourceInputSchema = z.discriminatedUnion("vcs", [z.object(gitInputShape).strict().refine(refAndShaAgree, REF_SHA_ISSUE), z.object({ vcs: z.literal("none") }).strict()]);
/**
* The app source record as the CP returns it on an app row (`source` of `GET /v1/apps`,
* `GET /v1/apps/:id` and `GET /v1/apps/ring-versions`): the input shape of
* {@link appSourceInputSchema} plus `detectedAt`, the CP's clock at the write.
*
* An app row carries `source: null` when nothing was ever detected, which is a different fact
* from `{ vcs: 'none', detectedAt }` (D7). This schema is the non-null value; a row field is
* `appSourceSchema.nullable()`.
*/
const appSourceSchema = z.discriminatedUnion("vcs", [z.object({
	...gitInputShape,
	detectedAt: detectedAtSchema
}).strict().refine(refAndShaAgree, REF_SHA_ISSUE), z.object({
	vcs: z.literal("none"),
	detectedAt: detectedAtSchema
}).strict()]);
/**
* The SDK versions one release built against: a record from a package name to its resolved
* version. The `@zion/*` names come from the app's own importer in `pnpm-lock.yaml` (D14). The
* name {@link APP_SDK_PYTHON_PACKAGE} comes from a `uv.lock` and the name
* {@link APP_SDK_RUST_PACKAGE} from a `Cargo.lock` (plan 2026-10-18-dynamic-blob-eligibility, D3a).
* At most {@link APP_SDK_VERSIONS_MAX_ENTRIES} entries. Each key matches
* {@link APP_SDK_NAME_PATTERN}, so a `constructor` key and any third non-npm name are refused. zod 4.4 skips an own `__proto__` key with no issue (test
* AS-74b), so a door that must refuse one checks the raw input itself, as `sdkVersionsOf` in
* release-engine's `build-facts.ts` does. Each value is a version of at most
* {@link APP_SDK_VERSION_MAX} characters that matches {@link APP_SDK_VERSION_PATTERN}. An empty
* record is valid: the app had no SDK dependency.
*/
const appSdkVersionsSchema = z.record(z.string().regex(APP_SDK_NAME_PATTERN), z.string().max(64).regex(APP_SDK_VERSION_PATTERN)).refine((versions) => Object.keys(versions).length <= 64, { message: `an SDK versions record holds at most 64 entries` }).describe("The SDK versions of the app: @zion/* npm packages, zion-sdk and zion-sdk-runtime, name to version.");
/**
* The per-release build facts as the CP returns them on a version row (`release.app_compositions`
* via `GET /v1/releases/apps/:appId/versions`) and on a ring entry of
* `GET /v1/apps/ring-versions` (C-2, D13). Each field is null for a release before this feature,
* for a release from an old CLI, and for an annotation the parser refused (D15).
*
* - `sourceSha`: the commit the bundle was built from, 40 or 64 lower-case hex.
* - `sourceDirty`: true when the checkout had uncommitted changes at the build.
* - `sdkVersions`: {@link appSdkVersionsSchema}.
*/
const appReleaseFactsSchema = z.object({
	sourceSha: appSourceShaSchema.nullable(),
	sourceDirty: z.boolean().nullable(),
	sdkVersions: appSdkVersionsSchema.nullable()
}).strict();
//#endregion
export { APP_SDK_NAME_PATTERN, APP_SDK_PACKAGE_PATTERN, APP_SDK_PYTHON_PACKAGE, APP_SDK_RUST_PACKAGE, APP_SDK_VERSIONS_MAX_ENTRIES, APP_SDK_VERSION_MAX, APP_SDK_VERSION_PATTERN, APP_SOURCE_BRANCH_REF_PREFIX, APP_SOURCE_DETACHED_REF, APP_SOURCE_ORIGIN_MAX, APP_SOURCE_PATH_MAX, APP_SOURCE_REF_MAX, APP_SOURCE_REPO_MAX, APP_SOURCE_REPO_PATTERN, APP_SOURCE_SHA_PATTERN, APP_SOURCE_VCS, appReleaseFactsSchema, appSdkVersionsSchema, appSourceInputSchema, appSourceOriginParts, appSourceSchema, appSourceShaSchema, isAppSourceOrigin, isAppSourcePath, isAppSourceRef, isAppSourceRepo };

//# sourceMappingURL=app-source.js.map